import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useTranslation } from "react-i18next"
import formatDateTime from "components/Shared/formatDateTime"
import { getTemplateById } from "store/actions/templatesAction"

const TemplateDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Gift Card Template Details ")
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const cardTypeId = searchParams.get("id")

  const { loading, template } = useSelector(state => state.Templates)

  useEffect(() => {
    if (cardTypeId) {
      getTemplateDetails()
    }
  }, [cardTypeId])

  const getTemplateDetails = () => {
    dispatch(getTemplateById({ cardTypeId: cardTypeId }))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={12}>
                  <ShimmerThumbnail height={600} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 d-flex align-items-center">
                    <BackButton /> {t("Gift Card Template Details")}
                  </CardTitle>
                  <div className="table-responsive">
                    {!template || Object.keys(template).length === 0 ? (
                      <p className="text-center">
                        {t("No template details found")}
                      </p>
                    ) : (
                      <Table className="table mb-0">
                        <tbody>
                          <tr>
                            <th>
                              <h5>Sender Screen</h5>
                            </th>
                            <th></th>
                          </tr>
                          <tr>
                            <th scope="row">{t("Card Title (English)")}</th>
                            <td className="text-wrap">
                              {template?.card_title_on_app_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Card Title (Arabic)")}</th>
                            <td className="text-wrap text-start">
                              {template?.card_title_on_app_ar || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Share Title (English)")}</th>
                            <td className="text-wrap">
                              {template?.share_gift_card_title_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Share Title (Arabic)")}</th>
                            <td className="text-wrap text-start">
                              {template?.share_gift_card_title_ar || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Share Description (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.share_gift_card_desc_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Share Description (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.share_gift_card_desc_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th>
                              <h5>Payment Screen</h5>
                            </th>
                            <th></th>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Purchase Popup Title (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.purchased_gift_card_popup_title_en ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Purchase Popup Title (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.purchased_gift_card_popup_title_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Purchase Popup Description (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.purchased_gift_card_popup_desc_en ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Purchase Popup Description (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.purchased_gift_card_popup_desc_ar ||
                                "-"}
                            </td>
                          </tr>

                          <tr>
                            <th>
                              <h5>Receiver Notification</h5>
                            </th>
                            <th></th>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("First Dialog Box Title (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.confirm_redeem_card_title_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("First Dialog Box Title (عربي)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.confirm_redeem_card_title_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("First Dialog Box Message Body (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.confirm_redeem_card_message_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("First Dialog Box Message Body (عربي)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.confirm_redeem_card_message_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Gift Received Title (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.card_rcvd_title_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Gift Received Title  (عربي)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.card_rcvd_title_ || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Gift Received Message (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.card_rcvd_msg_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Gift Received Message  (عربي)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.card_rcvd_msg_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Second Dialog Box Title (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.redeem_gift_card_title_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Second Dialog Box Title (عربي)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.redeem_gift_card_title_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Second Dialog Box Message Body (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.redeem_gift_card_message_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Second Dialog Box Message Body (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.redeem_gift_card_message_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("Redeem Now Button (English)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.redeem_now_button_en || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">{t("Redeem Now Button (عربي)")}</th>
                            <td className="text-wrap text-start">
                              {template?.redeem_now_button_ar || "-"}
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">
                              {t("App Notification Title (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.gift_card_received_appnotify_title_en ||
                                "-"}
                              <td className="text-wrap text-start">
                                {template?.purchased_gift_card_popup_desc_ar ||
                                  "-"}
                              </td>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("App Notification Title (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.gift_card_received_appnotify_title_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("App Notification Description (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.gift_card_received_appnotify_desc_en ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("App Notification Description (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.gift_card_received_appnotify_desc_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("SMS Message (English)")}</th>
                            <td className="text-wrap">
                              {template?.sms_message_for_receiver_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t(
                                "Add to Wallet App Notification Title (English)"
                              )}
                            </th>
                            <td className="text-wrap">
                              {template?.add_to_wallet_app_notify_title_en ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t(
                                "Add to Wallet App Notification Title (Arabic)"
                              )}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.add_to_wallet_app_notify_title_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t(
                                "Add to Wallet App Notification Message (English)"
                              )}
                            </th>
                            <td className="text-wrap">
                              {template?.add_to_wallet_app_notify_msg_en || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t(
                                "Add to Wallet App Notification Message (Arabic)"
                              )}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.add_to_wallet_app_notify_msg_ar || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("SMS Message (Arabic)")}</th>
                            <td className="text-wrap text-start">
                              {template?.sms_message_for_receiver_ar || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Social Media Message (English)")}
                            </th>
                            <td className="text-wrap">
                              {template?.socialmedia_message_for_receiver_en ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Social Media Message (Arabic)")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.socialmedia_message_for_receiver_ar ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              {t("Whatsapp Message Template Name")}
                            </th>
                            <td className="text-wrap text-start">
                              {template?.whatsapp_message_template || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Popup Image")}</th>
                            <td className="text-wrap text-start">
                              <img
                                src={template?.popup_image}
                                height={100}
                                width={100}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default TemplateDetails
